import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Leaf, Phone, KeyRound, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'

import { useAuth } from '../auth/AuthProvider'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { FormField } from '../components/FormField'

type Step = 'phone' | 'otp'

export function LoginPage() {
  const navigate = useNavigate()
  const { sendOtp, verifyOtp } = useAuth()
  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [devOtp, setDevOtp] = useState<string | undefined>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSendOtp(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    const r = await sendOtp(phone.trim())
    setBusy(false)
    if (!r.ok) {
      setError(r.error)
      return
    }
    setDevOtp(r.devOtp)
    setStep('otp')
    if (r.devOtp) toast.info(`Dev OTP: ${r.devOtp}`, { duration: 8000 })
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    const r = await verifyOtp(phone.trim(), otp.trim())
    setBusy(false)
    if (!r.ok) {
      setError(r.error)
      return
    }
    toast.success('Welcome back')
    navigate('/dashboard', { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto size-12 rounded-xl bg-forest flex items-center justify-center mb-3">
            <Leaf className="size-6 text-white" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-ink">DrHerbal Admin</h1>
          <p className="text-sm text-slatey mt-1">Operational console · sign in with your admin phone</p>
        </div>

        <div className="card p-6">
          {step === 'phone' ? (
            <form onSubmit={onSendOtp} className="space-y-4">
              <FormField label="Phone number" required>
                <div className="relative">
                  <Phone className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-slatey" />
                  <Input
                    type="tel"
                    placeholder="+91XXXXXXXXXX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-9"
                    required
                    autoFocus
                  />
                </div>
              </FormField>
              {error ? <p className="text-xs text-danger">{error}</p> : null}
              <Button type="submit" loading={busy} className="w-full">
                Send OTP
                <ArrowRight className="size-4" />
              </Button>
              <p className="text-xs text-slatey text-center">
                Dev tip: use <span className="font-mono text-ink">+918000000000</span> with OTP{' '}
                <span className="font-mono text-ink">123456</span>
              </p>
            </form>
          ) : (
            <form onSubmit={onVerify} className="space-y-4">
              <FormField label="Enter 6-digit OTP" required hint={`Sent to ${phone}`}>
                <div className="relative">
                  <KeyRound className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-slatey" />
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    className="pl-9 font-mono tracking-widest"
                    required
                    autoFocus
                  />
                </div>
              </FormField>
              {devOtp ? (
                <p className="text-xs font-mono text-forest">Dev OTP: {devOtp}</p>
              ) : null}
              {error ? <p className="text-xs text-danger">{error}</p> : null}
              <Button type="submit" loading={busy} className="w-full">
                Verify & Sign in
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('phone')
                  setOtp('')
                  setError(null)
                }}
                className="block mx-auto text-xs text-slatey hover:text-ink"
              >
                ← Change phone number
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}